/**
 * Where a skill lands, resolved the way the server resolves it.
 *
 * A skill's area is not a radius. It is `spray_config.spray` — a small matrix laid
 * over the target cell and turned to face the aim — and `SkillTargetingCalculator`
 * on the server does exactly this before `TacticalTargetResolver` populates
 * `action.targets`. The client has to agree with it cell for cell, or the preview is
 * a lie: the player aims at one shape and the fight resolves another.
 *
 * ---------------------------------------------------------------------------
 * THE CLIENT AND THE SERVER DO NOT CURRENTLY AGREE. Two divergences, both real:
 *
 *   1. ANCHOR. The server pins the pattern by its geometric centre
 *      (`pattern_height // 2`, apps/game/services/fight/targeting.py:143) and
 *      ignores the `x` marker when placing it. PlayerTacticalMap pins it by the `x`
 *      pivot. For any pattern whose pivot is not the middle cell the two land on
 *      different ground, and the preview is wrong by that offset.
 *
 *   2. RANGE. `SkillTargetingCalculator.validate_range` measures Manhattan
 *      distance; the map measures Chebyshev. A diagonal target reads as in range on
 *      the board and out of range on the server.
 *
 * The server is the authority — it is what actually resolves the fight — so
 * `anchor: 'center'` and `metric: 'manhattan'` are the defaults here. The map's
 * present behaviour is still reachable as `anchor: 'pivot'` / `metric: 'chebyshev'`
 * so the difference can be shown side by side rather than argued about. Which one
 * the game SHOULD use is a design decision, not something to quietly change under
 * a rendering task.
 * ---------------------------------------------------------------------------
 */

export function gridDistance(ax, ay, bx, by, metric = 'manhattan') {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return metric === 'chebyshev' ? Math.max(dx, dy) : dx + dy;
}

/** Cells the pattern marks as hit. `o` marks an anchor that is deliberately spared. */
const AFFECTED = new Set([1, '1', 'x']);

/**
 * How many cells the skill was AUTHORED to cover, regardless of where it is aimed.
 *
 * The resolved cell count cannot answer "is this a place or a blow": a wall clips a
 * nine-cell nova down to one, and the skill is still a nova.
 */
export function sprayCellCount(spray) {
  if (!Array.isArray(spray)) return 0;
  return spray.reduce((total, row) => (
    total + (Array.isArray(row) ? row.filter(value => AFFECTED.has(value)).length : 0)
  ), 0);
}

export function findSprayPivot(spray) {
  for (let row = 0; row < spray.length; row++) {
    for (let col = 0; col < spray[row].length; col++) {
      const value = spray[row][col];
      if (value === 'x' || value === 'o') return { col, row };
    }
  }
  return null;
}

/** The server's rotation: offsets turned clockwise about the anchor, not the matrix. */
export function rotateOffset(dx, dy, rotation) {
  const turns = ((Math.round(rotation / 90) % 4) + 4) % 4;
  // `|| 0` only to keep negative zero out of the result: it is arithmetically
  // harmless and confusing in every log and assertion that ever prints it.
  if (turns === 1) return { dx: -dy || 0, dy: dx || 0 };
  if (turns === 2) return { dx: -dx || 0, dy: -dy || 0 };
  if (turns === 3) return { dx: dy || 0, dy: -dx || 0 };
  return { dx: dx || 0, dy: dy || 0 };
}

/** Caster to target, snapped to the four facings, exactly as the server snaps it. */
export function rotationToward(caster, target) {
  const dx = target.col - caster.col;
  const dy = target.row - caster.row;
  if (!dx && !dy) return 0;
  const degrees = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
  if (degrees < 45) return 0;
  if (degrees < 135) return 90;
  if (degrees < 225) return 180;
  return 270;
}

/** The targeting a skill carries, whether it arrived bare, on an item, or as a special. */
export function skillTargeting(ability) {
  if (!ability) return null;
  const skill = ability.spray_config ? ability : (ability.item?.skill || ability.skill || null);
  if (!skill) return null;
  const spray = skill.spray_config?.spray;
  return {
    spray: spray && spray.length ? spray : [['x']],
    rotatable: Boolean(skill.spray_config?.rotatable),
    minRange: skill.min_range ?? 0,
    maxRange: skill.max_range ?? Infinity,
    charges: skill.charge_count ?? 1,
    multiTarget: Boolean(skill.multi_target),
  };
}

/**
 * The cells an aim covers.
 *
 * `passable` is the board's own answer about what is floor. A pattern clipped
 * against it is the difference between showing a shape and showing a shot — and it
 * is why the live board runs with clipping on while the sandbox runs with it off.
 */
export function resolveAim({
  ability,
  target,
  rotation = null,
  caster = null,
  columns = Infinity,
  rows = Infinity,
  passable = null,
  anchor = 'center',
  metric = 'manhattan',
}) {
  const targeting = skillTargeting(ability);
  if (!targeting || !target) {
    return { cells: [], blocked: [], inRange: false, distance: null, rotation: 0, targeting: null };
  }

  const { spray } = targeting;
  const height = spray.length;
  const width = spray[0].length;

  let facing = 0;
  if (targeting.rotatable) {
    facing = rotation === null
      ? (caster ? rotationToward(caster, target) : 0)
      : rotation;
  }

  let anchorCol = Math.floor(width / 2);
  let anchorRow = Math.floor(height / 2);
  if (anchor === 'pivot') {
    const pivot = findSprayPivot(spray);
    if (pivot) {
      anchorCol = pivot.col;
      anchorRow = pivot.row;
    }
  }

  const distance = caster
    ? gridDistance(caster.col, caster.row, target.col, target.row, metric)
    : 0;
  const inRange = !caster || (distance >= targeting.minRange && distance <= targeting.maxRange);

  const cells = [];
  const blocked = [];
  const seen = new Set();

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const value = spray[row][col];
      if (!AFFECTED.has(value)) continue;
      const turned = rotateOffset(col - anchorCol, row - anchorRow, facing);
      const cell = {
        col: target.col + turned.dx,
        row: target.row + turned.dy,
        isAnchor: value === 'x',
      };
      const key = `${cell.col},${cell.row}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (cell.col < 0 || cell.row < 0 || cell.col >= columns || cell.row >= rows) continue;
      if (passable && !passable(cell.col, cell.row)) {
        blocked.push(cell);
        continue;
      }
      cells.push(cell);
    }
  }

  return { cells, blocked, inRange, distance, rotation: facing, targeting };
}
